import React, { useState, useEffect, useRef } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { JobHttpService } from 'cms/features/feature_job/service';
import { HttpService } from 'core/services/http';
import { Utils } from 'core/utils';
import {
    Box,
    Card,
    CardContent,
    Typography,
    IconButton,
    Button,
    CircularProgress,
    Chip,
    Tooltip,
    Avatar
} from '@mui/material';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BlockIcon from '@mui/icons-material/Block';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';

const AVATAR_COLORS = [
    '#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#c62828',
    '#00796b', '#5d4037', '#0288d1', '#512da8', '#d81b60'
];

function nameToColor(name) {
    let hash = 0;
    for (let i = 0; i < (name?.length || 0); i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getCompanyLogoUrl(company) {
    if (!company?.image) return null;
    if (company.image.startsWith('http')) return company.image;
    if (company.image.startsWith('/')) return HttpService.SiteBase + company.image;
    return `${HttpService.SiteBase}/${company.image}`;
}

function JobsListSidebar({ searchFilters = {}, onLoadingChange }) {
    const [hiddenJobIds, setHiddenJobIds] = useState(new Set());
    const observerTarget = useRef(null);
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const size = 20;

    const { data, isLoading, isFetching, hasNextPage, fetchNextPage } = useInfiniteQuery(
        ['jobsList', searchFilters],
        async ({ pageParam = 1 }) => {
            return await JobHttpService.loadJobs({ ...searchFilters, page: pageParam, size });
        },
        {
            getNextPageParam: (lastPage, allPages) => {
                const totalLoaded = allPages.reduce((sum, p) => sum + (p.results?.length || 0), 0);
                return totalLoaded < (lastPage.count || 0) ? allPages.length + 1 : undefined;
            },
            keepPreviousData: true,
            staleTime: 30000
        }
    );

    const jobs = data?.pages?.flatMap(p => p.results || []) || [];
    const visibleJobs = jobs.filter(job => !hiddenJobIds.has(job.id));

    useEffect(() => {
        if (onLoadingChange) onLoadingChange(isLoading || isFetching);
    }, [isLoading, isFetching, onLoadingChange]);

    const bookmarkMutation = useMutation(
        async (jobId) => new HttpService().post('/job/bookmark/', { job: jobId }),
        { onSuccess: () => queryClient.invalidateQueries(['jobsList']) }
    );

    const hideJobMutation = useMutation(
        async (jobId) => new HttpService().post('/job/hide/', { job: jobId }),
        { onSuccess: (_, jobId) => setHiddenJobIds(prev => new Set([...prev, jobId])) }
    );

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting && hasNextPage && !isFetching) fetchNextPage(); },
            { threshold: 0.1 }
        );
        const el = observerTarget.current;
        if (el) observer.observe(el);
        return () => { if (el) observer.unobserve(el); };
    }, [hasNextPage, isFetching, fetchNextPage]);

    const getLocation = (job) => {
        const parts = [];
        if (job.company?.city) parts.push(job.company.city);
        if (job.company?.country?.name) parts.push(Utils.getDefaultName(job.company.country));
        return parts.join(', ') || job.address || null;
    };

    const getSalary = (job) => {
        if (job.salary) return `$${job.salary}${job.pay_rate ? `/${job.pay_rate}` : ''}`;
        if (job.pay_minimum && job.pay_maximum)
            return `$${job.pay_minimum}–$${job.pay_maximum}${job.pay_rate ? `/${job.pay_rate}` : ''}`;
        return null;
    };

    if (isLoading && !data) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (visibleJobs.length === 0 && !isLoading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 1 }}>
                <WorkOutlineIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                <Typography variant="subtitle1" color="text.secondary">
                    No jobs found. Try adjusting your filters.
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {data?.pages?.[0]?.count || 0} jobs found
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {visibleJobs.map((job) => {
                    const isBookmarked = job.is_bookmarked || false;
                    const salary = getSalary(job);
                    const location = getLocation(job);
                    const logoUrl = getCompanyLogoUrl(job.company);
                    const companyTitle = job.company?.title || '';
                    const initial = companyTitle.charAt(0).toUpperCase() || '?';

                    return (
                        <Card
                            key={job.id}
                            className="jf-job-card"
                            onClick={() => navigate(`/job/${job.id}`)}
                            sx={{ cursor: 'pointer', '&:hover .job-actions': { opacity: 1 } }}
                        >
                            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                    <Avatar
                                        variant="rounded"
                                        src={logoUrl || undefined}
                                        alt={companyTitle}
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            flexShrink: 0,
                                            bgcolor: logoUrl ? 'grey.100' : nameToColor(companyTitle),
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            fontSize: '0.875rem',
                                            fontWeight: 700,
                                            color: '#fff',
                                            '& .MuiAvatar-img': { objectFit: 'contain', objectPosition: 'center' }
                                        }}
                                    >
                                        {initial}
                                    </Avatar>

                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 0.5 }}>
                                            <Typography
                                                variant="subtitle1"
                                                fontWeight={600}
                                                sx={{
                                                    lineHeight: 1.3,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical'
                                                }}
                                            >
                                                {Utils.normalizeText(job.title)}
                                            </Typography>
                                            <Box
                                                className="job-actions"
                                                sx={{
                                                    display: 'flex',
                                                    flexShrink: 0,
                                                    opacity: isBookmarked ? 1 : 0,
                                                    transition: 'opacity 0.15s'
                                                }}
                                            >
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => { e.stopPropagation(); bookmarkMutation.mutate(job.id); }}
                                                    color={isBookmarked ? 'primary' : 'default'}
                                                    sx={{ p: 0.5 }}
                                                >
                                                    {isBookmarked ? <BookmarkIcon fontSize="small" /> : <BookmarkBorderIcon fontSize="small" />}
                                                </IconButton>
                                                <Tooltip title="Hide job">
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => { e.stopPropagation(); hideJobMutation.mutate(job.id); }}
                                                        sx={{ p: 0.5, color: 'text.disabled', '&:hover': { color: 'text.secondary' } }}
                                                    >
                                                        <BlockIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>

                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                            {companyTitle || 'Unknown Company'}
                                        </Typography>

                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75, flexWrap: 'wrap' }}>
                                            {location && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                                    <LocationOnIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                                                    <Typography variant="caption" color="text.secondary">{location}</Typography>
                                                </Box>
                                            )}
                                            {salary && (
                                                <>
                                                    {location && <Typography variant="caption" color="text.disabled">·</Typography>}
                                                    <Typography variant="caption" fontWeight={600} color="success.dark">{salary}</Typography>
                                                </>
                                            )}
                                        </Box>

                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.75, flexWrap: 'wrap', gap: 0.5 }}>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {job.jobtypes?.slice(0, 2).map((type) => (
                                                    <Chip
                                                        key={type.id}
                                                        label={Utils.normalizeText(type.title)}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ fontSize: '0.72rem', height: '24px' }}
                                                    />
                                                ))}
                                                {job.schedules?.slice(0, 1).map((schedule) => (
                                                    <Chip
                                                        key={schedule.id}
                                                        label={Utils.normalizeText(schedule.title)}
                                                        size="small"
                                                        variant="outlined"
                                                        color="primary"
                                                        sx={{ fontSize: '0.72rem', height: '24px' }}
                                                    />
                                                ))}
                                            </Box>
                                            <Typography variant="caption" color="text.disabled">
                                                {Utils.timeAgo(job.updated_at)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    );
                })}
            </Box>

            {hasNextPage && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <Button variant="outlined" onClick={() => fetchNextPage()} disabled={isFetching} fullWidth>
                        {isFetching ? <CircularProgress size={20} /> : 'Load more'}
                    </Button>
                </Box>
            )}

            <Box ref={observerTarget} sx={{ height: 20, mt: 2 }} />

            {isFetching && hasNextPage && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <CircularProgress size={24} />
                </Box>
            )}
        </Box>
    );
}

export default JobsListSidebar;
